import { getDataGridToolbarProps } from 'features/shared/utility/dataGridToolbarHelper';
import usePermissions from 'features/shared/hooks/usePermissions';
import { PERMISSION_RESOURCES } from 'features/shared/constants/permissionResources';
import FormSpinner from 'components/FormSpinner';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Box } from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import AddToPhotosIcon from '@material-ui/icons/AddToPhotos';
import { CommonDataGrid } from 'features/shared/components/table';
import { RMSConfirmationDialog } from '../../../shared/components/dialog';
import { RMSButton } from '../../../shared/components/buttons';
import { executeApiAction } from '../../../shared/utility/apiHelper';
import { notify } from 'reducers/NotifierReducer';
import useConfirmDialog from '../../../shared/utility/useConfirmDialog';
import PersonFeatureDialog from './PersonFeatureDialog';
import PersonMultipleFeatureDialog from 'features/user/components/add-edit-person/PersonMultipleFeatureDialog';
import { getPeopleFeatures, deletePersonFeature } from '../../services/userService';
import { FEATURE_GRID_COLUMNS } from '../../constants/personFeatureFormConstants';

const PersonFeaturesTab = ({ personId }) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [features, setFeatures] = useState([]);
  const [formOpen, setFormOpen] = useState(false);
  const [multipleFormOpen, setMultipleFormOpen] = useState(false);
  const [editingFeature, setEditingFeature] = useState(null);
  const canEditPerson = usePermissions(PERMISSION_RESOURCES.PERSON).Edit;
  const { confirmState, requestConfirm, handleYes, handleNo } = useConfirmDialog();

  const loadFeatures = useCallback(async () => {
    if (!personId) return;

    await executeApiAction({
      setLoading,
      dispatch,
      action: () => getPeopleFeatures(personId),
      onSuccess: (data) => setFeatures(data),
    });
  }, [dispatch, personId]);

  const handleAdd = useCallback(() => {
    setEditingFeature(null);
    setFormOpen(true);
  }, []);

  const handleEdit = useCallback((row) => {
    setEditingFeature(row);
    setFormOpen(true);
  }, []);

  const handleFormClose = useCallback(() => {
    setFormOpen(false);
    setEditingFeature(null);
  }, []);

  const handleSaved = useCallback(() => {
    handleFormClose();
    loadFeatures();
  }, [handleFormClose, loadFeatures]);

  const handleMultipleOpen = useCallback(() => setMultipleFormOpen(true), []);
  const handleMultipleClose = useCallback(() => setMultipleFormOpen(false), []);

  const handleMultipleSaved = useCallback(() => {
    setMultipleFormOpen(false);
    loadFeatures();
  }, [loadFeatures]);

  const handleDelete = useCallback(
    (row) => {
      requestConfirm(
        `Are you sure you want to permanently delete this "${
          row.AttributeCategory || 'feature'
        }" record?`,
        async () => {
          await executeApiAction({
            setLoading,
            dispatch,
            action: () => deletePersonFeature(row.ptsFeatureID),
            onSuccess: () => {
              dispatch(notify('Feature deleted successfully', 'success'));
              loadFeatures();
            },
          });
        },
        'Delete Feature'
      );
    },
    [dispatch, requestConfirm, loadFeatures]
  );

  useEffect(() => {
    loadFeatures();
  }, [loadFeatures]);

  return (
    <Box className="person-names-tab__grid-wrap">
      {loading && <FormSpinner />}
      <Box className="person-addresses-tab__toolbar">
        <RMSButton
          label="Add Multiple"
          variant="outlined"
          prefixIcon={<AddToPhotosIcon />}
          onClick={handleMultipleOpen}
          disabled={!canEditPerson}
        />
        <RMSButton
          label="Add Feature"
          prefixIcon={<AddIcon />}
          onClick={handleAdd}
          disabled={!canEditPerson}
        />
      </Box>

      <CommonDataGrid
        rows={features}
        columns={FEATURE_GRID_COLUMNS}
        getRowId={(row) => row.ptsFeatureID}
        toolbar={getDataGridToolbarProps({ printTitle: 'Features' })}
        actions={{
          edit: { visible: true, onClick: handleEdit, disabled: !canEditPerson },
          delete: { visible: true, onClick: handleDelete, disabled: !canEditPerson },
        }}
      />

      {formOpen && (
        <PersonFeatureDialog
          open={formOpen}
          personId={personId}
          feature={editingFeature}
          onClose={handleFormClose}
          onSaved={handleSaved}
        />
      )}

      {multipleFormOpen && (
        <PersonMultipleFeatureDialog
          open={multipleFormOpen}
          personId={personId}
          onClose={handleMultipleClose}
          onSaved={handleMultipleSaved}
        />
      )}

      {confirmState.open && (
        <RMSConfirmationDialog
          open={confirmState.open}
          title={confirmState.title}
          message={confirmState.message}
          onNo={handleNo}
          onYes={handleYes}
        />
      )}
    </Box>
  );
};

export default PersonFeaturesTab;
